import React, { useState, useEffect } from 'react';
import { 
  Modal,
  Card, 
  Button, 
  Typography, 
  Space, 
  Spin, 
  message, 
  Tag,
  Form,
  Input,
  Select,
  Divider,
  Alert,
  Row,
  Col
} from 'antd';
import { 
  SlackOutlined, 
  LinkOutlined, 
  DisconnectOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import apiClient from '../services/axiosConfig';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const IntegrationsModal = ({ visible, onClose, userId, userEmail, userName, theme, currentTheme }) => {
  const [loading, setLoading] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [slackChannels, setSlackChannels] = useState([]);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [jiraEpics, setJiraEpics] = useState([]);
  const [loadingEpics, setLoadingEpics] = useState(false);
  const [jiraModalVisible, setJiraModalVisible] = useState(false);
  const [slackSettingsVisible, setSlackSettingsVisible] = useState(false);
  const [jiraSettingsVisible, setJiraSettingsVisible] = useState(false);
  const [connectingSlack, setConnectingSlack] = useState(false);
  const [connectingJira, setConnectingJira] = useState(false);
  const [selectedProjectForSettings, setSelectedProjectForSettings] = useState(null);
  
  const [jiraForm] = Form.useForm();

  // Fetch integration status when modal opens
  useEffect(() => {
    if (visible && userId) {
      fetchIntegrationStatus();
    }
  }, [visible, userId]);

  // Fetch epics when Jira settings opens or project changes
  useEffect(() => {
    if (jiraSettingsVisible && selectedProjectForSettings) {
      fetchJiraEpics(selectedProjectForSettings);
    }
  }, [jiraSettingsVisible, selectedProjectForSettings]);

  const fetchIntegrationStatus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/integrations/status', {
        params: { userId, userEmail, userName }
      });
      setIntegrationStatus(response.data.data);
      
      // Set default selected project for settings
      if (response.data.data?.jira?.defaultProject?.key) {
        setSelectedProjectForSettings(response.data.data.jira.defaultProject.key);
      }
      
      return response.data.data;
    } catch (error) {
      message.error('Failed to fetch integration status');
      console.error(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchJiraEpics = async (projectKey) => {
    if (!projectKey) return;
    
    try {
      setLoadingEpics(true);
      const response = await apiClient.get(`/integrations/jira/projects/${projectKey}/epics`, {
        params: { userId }
      });
      setJiraEpics(response.data.epics || []);
    } catch (error) {
      console.error('Failed to fetch epics:', error);
      setJiraEpics([]);
    } finally {
      setLoadingEpics(false);
    }
  };

  // Slack connection
  const connectSlack = async () => {
    if (!userId || userId === 'demo-user') {
      message.error('Please log in to connect Slack');
      return;
    }
    
    try {
      setConnectingSlack(true);
      
      // Get current URL from browser (works with ngrok, localhost, or production)
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/integrations/slack/callback`;
      
      console.log('Slack OAuth redirect URI:', redirectUri);
      
      const response = await apiClient.post('/integrations/slack/connect', { 
        userId,
        redirectUri
      });
      
      // Open Slack OAuth in new window
      const authWindow = window.open(response.data.authUrl, 'slack-auth', 'width=600,height=700');
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        if (authWindow.closed) {
          clearInterval(pollInterval);
          setConnectingSlack(false);
          // Add a delay before refreshing to ensure callback completes
          setTimeout(async () => {
            const status = await fetchIntegrationStatus();
            if (status?.slack?.connected) {
              await fetchSlackChannels();
            }
          }, 2000);
        }
      }, 1000);
      
    } catch (error) {
      message.error('Failed to start Slack connection');
      console.error('Slack connection error:', error);
      setConnectingSlack(false);
    }
  };

  const disconnectSlack = async () => {
    Modal.confirm({
      title: 'Disconnect Slack?',
      content: 'You won\'t be able to send issues to Slack anymore.',
      onOk: async () => {
        try {
          await apiClient.post('/integrations/slack/disconnect', { userId });
          message.success('Slack disconnected');
          fetchIntegrationStatus();
        } catch (error) {
          message.error('Failed to disconnect Slack');
        }
      }
    });
  };

  const fetchSlackChannels = async () => {
    if (!integrationStatus?.slack?.connected) return;
    
    try {
      const response = await apiClient.get('/integrations/slack/channels', {
        params: { userId }
      });
      setSlackChannels(response.data.channels);
    } catch (error) {
      console.error('Failed to fetch Slack channels');
    }
  };

  // Jira connection
  const connectJira = async (values) => {
    try {
      setConnectingJira(true);
      const response = await apiClient.post('/integrations/jira/connect', {
        userId,
        ...values
      });
      
      message.success('Jira connected successfully');
      setJiraModalVisible(false);
      jiraForm.resetFields();
      setJiraProjects(response.data.projects);
      fetchIntegrationStatus();
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('Invalid Jira credentials');
      } else {
        message.error('Failed to connect Jira');
      }
    } finally {
      setConnectingJira(false);
    }
  };

  const disconnectJira = async () => {
    Modal.confirm({
      title: 'Disconnect Jira?',
      content: 'You won\'t be able to send issues to Jira anymore.',
      onOk: async () => {
        try {
          await apiClient.post('/integrations/jira/disconnect', { userId });
          message.success('Jira disconnected');
          fetchIntegrationStatus();
        } catch (error) {
          message.error('Failed to disconnect Jira');
        }
      }
    });
  };

  // Update default settings
  const updateSlackChannel = async (channelId) => {
    const channel = slackChannels.find(ch => ch.id === channelId);
    try {
      await apiClient.post('/integrations/slack/default-channel', {
        userId,
        channelId,
        channelName: channel.name
      });
      message.success('Default channel updated');
      fetchIntegrationStatus();
    } catch (error) {
      message.error('Failed to update default channel');
    }
  };

  const updateJiraDefaults = async (projectKey, epicKey) => {
    try {
      const project = integrationStatus?.jira?.availableProjects?.find(p => p.key === projectKey);
      const epic = jiraEpics.find(e => e.key === epicKey);
      
      await apiClient.post('/integrations/jira/default-project', {
        userId,
        project: project ? {
          key: project.key,
          name: project.name,
          id: project.id
        } : null,
        epic: epic ? {
          key: epic.key,
          name: epic.name,
          id: epic.id
        } : null
      });
      
      message.success('Default project and epic updated');
      fetchIntegrationStatus();
    } catch (error) {
      message.error('Failed to update defaults');
    }
  };

  return (
    <Modal
      title={
        <Space>
          <LinkOutlined />
          <span>Integrations</span>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={900}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Paragraph style={{ color: theme.textSecondary, marginBottom: 24 }}>
            Connect your Slack and Jira accounts to automatically send API issue reports to your team.
          </Paragraph>

          <Row gutter={24}>
            {/* Slack Integration */}
            <Col xs={24} md={12}>
              <Card
                title={
                  <Space>
                    <SlackOutlined style={{ fontSize: 24, color: '#4A154B' }} />
                    <span>Slack</span>
                  </Space>
                }
                extra={
                  integrationStatus?.slack?.connected ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">Not Connected</Tag>
                  )
                }
                style={{
                  background: theme.surface,
                  borderColor: theme.border,
                  marginBottom: 24
                }}
              >
                {integrationStatus?.slack?.connected ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ color: theme.text }}>Workspace: </Text>
                      <Text style={{ color: theme.textSecondary }}>
                        {integrationStatus.slack.teamName}
                      </Text>
                    </div>
                    <div>
                      <Text strong style={{ color: theme.text }}>Connected as: </Text>
                      <Text style={{ color: theme.textSecondary }}>
                        {integrationStatus.slack.userName}
                      </Text>
                    </div>
                    {integrationStatus.slack.defaultChannel && (
                      <div>
                        <Text strong style={{ color: theme.text }}>Default Channel: </Text>
                        <Text style={{ color: theme.textSecondary }}>
                          #{integrationStatus.slack.defaultChannel.name}
                        </Text>
                      </div>
                    )}
                    <Divider style={{ margin: '12px 0' }} />
                    <Space>
                      <Button 
                        icon={<SettingOutlined />}
                        onClick={() => {
                          fetchSlackChannels();
                          setSlackSettingsVisible(true);
                        }}
                      >
                        Settings
                      </Button>
                      <Button 
                        danger 
                        icon={<DisconnectOutlined />}
                        onClick={disconnectSlack}
                      >
                        Disconnect
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Paragraph style={{ color: theme.textSecondary }}>
                      Connect Slack to send issue reports directly to your team channels.
                    </Paragraph>
                    <Button 
                      type="primary" 
                      icon={<SlackOutlined />}
                      onClick={connectSlack}
                      loading={connectingSlack}
                      block
                    >
                      Connect Slack
                    </Button>
                  </Space>
                )}
              </Card>
            </Col>

            {/* Jira Integration */}
            <Col xs={24} md={12}>
              <Card
                title={
                  <Space>
                    <img 
                      src="https://cdn.worldvectorlogo.com/logos/jira-1.svg" 
                      alt="Jira" 
                      style={{ width: 24, height: 24 }} 
                    />
                    <span>Jira</span>
                  </Space>
                }
                extra={
                  integrationStatus?.jira?.connected ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">Not Connected</Tag>
                  )
                }
                style={{
                  background: theme.surface,
                  borderColor: theme.border,
                  marginBottom: 24
                }}
              >
                {integrationStatus?.jira?.connected ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ color: theme.text }}>Instance: </Text>
                      <Text style={{ color: theme.textSecondary }}>
                        {integrationStatus.jira.instanceUrl}
                      </Text>
                    </div>
                    <div>
                      <Text strong style={{ color: theme.text }}>Connected as: </Text>
                      <Text style={{ color: theme.textSecondary }}>
                        {integrationStatus.jira.displayName} ({integrationStatus.jira.email})
                      </Text>
                    </div>
                    {integrationStatus.jira.defaultProject && (
                      <div>
                        <Text strong style={{ color: theme.text }}>Default Project: </Text>
                        <Text style={{ color: theme.textSecondary }}>
                          {integrationStatus.jira.defaultProject.name} ({integrationStatus.jira.defaultProject.key})
                        </Text>
                      </div>
                    )}
                    {integrationStatus.jira.defaultEpic && (
                      <div>
                        <Text strong style={{ color: theme.text }}>Default Epic: </Text>
                        <Text style={{ color: theme.textSecondary }}>
                          {integrationStatus.jira.defaultEpic.name}
                        </Text>
                      </div>
                    )}
                    <Divider style={{ margin: '12px 0' }} />
                    <Space>
                      <Button 
                        icon={<SettingOutlined />}
                        onClick={() => {
                          setJiraSettingsVisible(true);
                          if (integrationStatus.jira.defaultProject?.key) {
                            fetchJiraEpics(integrationStatus.jira.defaultProject.key);
                          }
                        }}
                      >
                        Settings
                      </Button>
                      <Button 
                        danger 
                        icon={<DisconnectOutlined />}
                        onClick={disconnectJira}
                      >
                        Disconnect
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Paragraph style={{ color: theme.textSecondary }}>
                      Connect Jira to create issues for API problems automatically.
                    </Paragraph>
                    <Button 
                      type="primary" 
                      onClick={() => setJiraModalVisible(true)}
                      block
                    >
                      Connect Jira
                    </Button>
                  </Space>
                )}
              </Card>
            </Col>
          </Row>

          <Alert
            message="Security Note"
            description="Your credentials are encrypted and stored securely. We never share your integration data with third parties."
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />

          {/* Jira Connection Modal */}
          <Modal
            title="Connect Jira"
            visible={jiraModalVisible}
            onCancel={() => setJiraModalVisible(false)}
            footer={null}
            width={500}
          >
            <Form
              form={jiraForm}
              layout="vertical"
              onFinish={connectJira}
            >
              <Form.Item
                name="instanceUrl"
                label="Jira Instance URL"
                rules={[
                  { required: true, message: 'Please enter your Jira URL' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input 
                  placeholder="https://yourcompany.atlassian.net"
                  prefix={<LinkOutlined />}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="your-email@company.com" />
              </Form.Item>

              <Form.Item
                name="apiToken"
                label={
                  <span>
                    API Token{' '}
                    <a 
                      href="https://id.atlassian.com/manage-profile/security/api-tokens" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ fontSize: 12 }}
                    >
                      (Generate here)
                    </a>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter your API token' }]}
              >
                <Input.Password placeholder="Your Jira API token" />
              </Form.Item>

              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setJiraModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={connectingJira}>
                    Connect
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* Slack Settings Modal */}
          <Modal
            title="Slack Settings"
            visible={slackSettingsVisible}
            onCancel={() => setSlackSettingsVisible(false)}
            footer={[
              <Button key="close" onClick={() => setSlackSettingsVisible(false)}>
                Close
              </Button>
            ]}
          >
            <Form layout="vertical">
              <Form.Item label="Default Channel for Issues">
                <Select
                  value={integrationStatus?.slack?.defaultChannel?.id}
                  onChange={updateSlackChannel}
                  placeholder="Select a channel"
                  style={{ width: '100%' }}
                >
                  {slackChannels.map(channel => (
                    <Option key={channel.id} value={channel.id}>
                      #{channel.name} {channel.is_private && '🔒'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Modal>

          {/* Jira Settings Modal */}
          <Modal
            title="Jira Settings"
            visible={jiraSettingsVisible}
            onCancel={() => setJiraSettingsVisible(false)}
            footer={[
              <Button key="close" onClick={() => setJiraSettingsVisible(false)}>
                Close
              </Button>
            ]}
          >
            <Form 
              layout="vertical"
              initialValues={{
                defaultProject: integrationStatus?.jira?.defaultProject?.key,
                defaultEpic: integrationStatus?.jira?.defaultEpic?.key
              }}
            >
              <Form.Item label="Default Project for Issues">
                <Select
                  value={selectedProjectForSettings || integrationStatus?.jira?.defaultProject?.key}
                  onChange={(value) => {
                    setSelectedProjectForSettings(value);
                    fetchJiraEpics(value);
                  }}
                  placeholder="Select a project"
                  style={{ width: '100%' }}
                >
                  {integrationStatus?.jira?.availableProjects?.map(project => (
                    <Option key={project.key} value={project.key}>
                      {project.name} ({project.key})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item label="Default Epic">
                <Select
                  value={integrationStatus?.jira?.defaultEpic?.key}
                  onChange={(epicKey) => {
                    updateJiraDefaults(selectedProjectForSettings || integrationStatus?.jira?.defaultProject?.key, epicKey);
                  }}
                  placeholder="Select a default epic (optional)"
                  style={{ width: '100%' }}
                  loading={loadingEpics}
                  allowClear
                  disabled={!selectedProjectForSettings}
                >
                  {jiraEpics.map(epic => (
                    <Option key={epic.key} value={epic.key}>
                      {epic.name} ({epic.key})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Button 
                type="primary" 
                onClick={() => {
                  updateJiraDefaults(selectedProjectForSettings || integrationStatus?.jira?.defaultProject?.key, null);
                }}
              >
                Save Settings
              </Button>
            </Form>
          </Modal>
        </>
      )}
    </Modal>
  );
};

export default IntegrationsModal;